import React, { useState, useEffect } from 'react';
import { apiGetComments, apiAddComment, apiDeleteComment, getAuthUser } from '../services/api';

const Comments = ({ selectedFile, selectedTimeFrom, selectedTimeTo, selectedComment, onCommentsChange, highlightedCommentUuid, onCommentClick, websocketCommentOutdated }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [author, setAuthor] = useState(getAuthUser());
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [loadingComments, setLoading] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [deletingComment, setDeletingComment] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to sort comments by timeFrom
  const sortCommentsByTime = (commentsArray) => {
    return [...commentsArray].sort((a, b) => {
      const timeA = timeStringToSeconds(a.timeFrom || '00:00:00.000');
      const timeB = timeStringToSeconds(b.timeFrom || '00:00:00.000');
      return timeA - timeB; // Sort ascending (earliest first)
    });
  };

  // Helper function to convert hh:mm:ss.ms to seconds
  const timeStringToSeconds = (timeString) => {
    if (!timeString || timeString === '') return 0;
    
    const parts = timeString.split(':');
    if (parts.length !== 3) return 0;
    
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    const secondsAndMs = parseFloat(parts[2]) || 0;
    
    return hours * 3600 + minutes * 60 + secondsAndMs;
  };

  // Helper function to validate time format hh:mm:ss.ms
  const isValidTimeFormat = (timeString) => {
    if (!timeString) return true; // Empty is valid
    const timeRegex = /^([0-9]{1,2}):([0-5]?[0-9]):([0-5]?[0-9])\.([0-9]{1,3})$/;
    return timeRegex.test(timeString);
  };

  // Function to download comments file from API
  const downloadCommentsFile = async (filename) => {
    if (!filename) return;
    
    setAddingComment(false);
    setDeletingComment(false);
    setLoading(true);
    setError(null);
    
    try {
      const getCommentsResponse = await apiGetComments(filename);
      if (!getCommentsResponse.status || getCommentsResponse.status !== 200)
      {
        // File doesn't exist, start with empty comments
        console.log(getCommentsResponse);
        console.log('Comments file not found, starting with empty comments');
        setComments([]);
        return;
      }
      console.log(getCommentsResponse);
      
      // Step 3: Get the JSON content (already parsed by the API)
      const fileContent = await getCommentsResponse.data;
      const commentsData = fileContent;
      
      // Handle the new object format: {[{...}, {...}]}
      if (Array.isArray(commentsData)) {
        // A comment with NO timeFrom is a recording-wide "label" (managed in the
        // FileExplorer), not a timeline comment. Exclude it here so it never
        // appears as a waveform marker, in the comment list, or in the count.
        const realComments = commentsData.filter((c) => c && c.timeFrom);
        const sortedComments = sortCommentsByTime(realComments);
        setComments(sortedComments);
        console.log('Comments loaded from API (array format):', sortedComments);
      } else {
        setComments([]);
      }
      
    } catch (error) {
      console.error('Error downloading comments:', error);
      
      // Check if it's a JSON parse error vs file not found
      if (error.name === 'SyntaxError') {
        setError('Comments file contains invalid JSON');
      } else if (error.message.includes('404') || error.message.includes('not found')) {
        console.log('Comments file not found, starting with empty comments');
        setComments([]);
      } else {
        setError('Failed to load comments from server');
        setComments([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load comments when selectedFile changes
  useEffect(() => {
    if (selectedFile) {
      downloadCommentsFile(selectedFile);
    } else {
      setComments([]);
      setError(null);
    }
  }, [selectedFile]);


  // Reload comments when notified via WebSocket
  useEffect(() => {
    if (websocketCommentOutdated) {
      console.log('WebSocket notification: comments outdated for file', websocketCommentOutdated);
      if (websocketCommentOutdated.file === selectedFile)
      {
        downloadCommentsFile(selectedFile);
      }
    }
  }, [websocketCommentOutdated]);


  // Update time inputs when selectedTimeFrom/selectedTimeTo change (from WaveFormViewer clicks)
  useEffect(() => {
    if (selectedTimeFrom !== undefined && selectedTimeFrom !== null) {
      setTimeFrom(selectedTimeFrom);
    }
  }, [selectedTimeFrom]);

  useEffect(() => {
    if (selectedTimeTo !== undefined && selectedTimeTo !== null) {
      setTimeTo(selectedTimeTo);
    }
  }, [selectedTimeTo]);

  // Notify parent component when comments change
  useEffect(() => {
    if (onCommentsChange && typeof onCommentsChange === 'function') {
      onCommentsChange(comments);
    }
  }, [comments, onCommentsChange]);

  // Remove the localStorage save effect since we're using API now

  const addComment = async () => {
    if (newComment.trim() && author.trim()) {
      // Validate time formats
      if (timeFrom && !isValidTimeFormat(timeFrom)) {
        setError('Invalid time format for "From" field. Use hh:mm:ss.ms format (e.g., 01:23:45.678)');
        return;
      }
      if (timeTo && !isValidTimeFormat(timeTo)) {
        setError('Invalid time format for "To" field. Use hh:mm:ss.ms format (e.g., 01:23:45.678)');
        return;
      }
      
      setError(null); // Clear any previous errors
      
    const comment = {
        text: newComment.trim(),
        author: author.trim(),
        timeFrom: timeFrom || '00:00:00.000',
        timeTo: timeTo || '00:00:00.000',
        timestamp: new Date().toLocaleString(),
        relatedUuid: selectedComment ? selectedComment.uuid : null
        };
      
      setAddingComment(true);

      await apiAddComment(selectedFile, comment);

      setNewComment('');
      setTimeFrom('');
      setTimeTo('');

      // Reload directly instead of waiting for the WebSocket "comments updated"
      // push, which only reaches the file owner — grantees must refresh too.
      await downloadCommentsFile(selectedFile);
    }
  };

  const deleteComment = async (uuid) => {
    setDeletingComment(true);
    await apiDeleteComment(selectedFile, uuid);
    await downloadCommentsFile(selectedFile);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addComment();
    }
  };

  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '10px',
      padding: '1rem',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        borderBottom: '2px solid rgba(212, 175, 55, 1)',
        paddingBottom: '0.5rem'
      }}>
        <h3 style={{ 
          margin: 0, 
          fontSize: '0.85rem',
          fontWeight: '600',
          color: '#374151',
          textTransform: 'uppercase',
          letterSpacing: '0.08em'
        }}>
          Comments
        </h3>
        <button
          onClick={() => downloadCommentsFile(selectedFile)}
          disabled={!selectedFile || loadingComments}
          style={{
            background: 'none',
            border: 'none',
            cursor: selectedFile && !loadingComments ? 'pointer' : 'not-allowed',
            fontSize: '1.2rem',
            padding: '0.25rem',
            color: 'rgba(212, 175, 55, 1)',
            opacity: selectedFile && !loadingComments ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Reload comments"
        >
          🔄
        </button>
      </div>

      {/* Loading State */}
      {loadingComments && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          padding: '0.5rem',
          marginBottom: '1rem',
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#856404'
        }}>
          Loading comments...
        </div>
      )}

      {/* Loading State */}
      {addingComment && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          padding: '0.5rem',
          marginBottom: '1rem',
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#856404'
        }}>
          Adding comment...
        </div>
      )}

      {/* Loading State */}
      {deletingComment && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          padding: '0.5rem',
          marginBottom: '1rem',
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#856404'
        }}>
          Deleting comment...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          padding: '0.5rem',
          marginBottom: '1rem',
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#721c24'
        }}>
          {error}
        </div>
      )}

      {/* Add Comment Form */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Your name"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          style={{
            width: '94%',
            padding: '0.5rem',
            marginBottom: '0.5rem',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}
        />
        
        {/* Time Range Inputs */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '0.5rem',
          alignItems: 'center'
        }}>
          <input
            type="text"
            placeholder="(hh:mm:ss.ttt)"
            value={timeFrom}
            onChange={(e) => setTimeFrom(e.target.value)}
            style={{
              flex: 1,
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              width: '90%',
              fontSize: '0.9rem'
            }}
          />
          <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>to</span>
          <input
            type="text"
            placeholder="(hh:mm:ss.ttt)"
            value={timeTo}
            onChange={(e) => setTimeTo(e.target.value)}
            style={{
              flex: 1,
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              width: '90%',
              fontSize: '0.9rem'
            }}
          />
        </div>
        
        <textarea
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={handleKeyPress}
          rows={3}
          style={{
            width: '94%',
            padding: '0.5rem',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            resize: 'vertical',
            fontSize: '0.9rem',
            fontFamily: 'inherit'
          }}
        />
        <button
          onClick={addComment}
          disabled={!newComment.trim() || !author.trim()}
          style={{
            backgroundColor: 'rgba(212, 175, 55, 1)',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: newComment.trim() && author.trim() ? 'pointer' : 'not-allowed',
            fontSize: '0.9rem',
            marginTop: '0.5rem',
            opacity: newComment.trim() && author.trim() ? 1 : 0.6
          }}
        >
          Add Comment
        </button>
      </div>

      {/* Comments List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        maxHeight: '400px'
      }}>
        {comments.length === 0 ? (
          <p style={{ 
            color: '#6c757d', 
            fontStyle: 'italic',
            textAlign: 'center',
            marginTop: '2rem'
          }}>
            No comments yet. Be golden!
          </p>
        ) : (
          comments.map(comment => {
            const isHighlighted = highlightedCommentUuid === comment.uuid;
            return (
              <div
                key={comment.uuid}
                onClick={() => {
                  if (onCommentClick) {
                    onCommentClick(comment);
                  }
                }}
                style={{
                  backgroundColor: isHighlighted ? 'rgba(212, 175, 55, 0.1)' : 'white',
                  border: isHighlighted ? '2px solid rgba(212, 175, 55, 0.8)' : '1px solid #f0f0f0',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  marginBottom: '0.75rem',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  boxShadow: isHighlighted ? '0 2px 8px rgba(212, 175, 55, 0.2)' : 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!isHighlighted) {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isHighlighted) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
              >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.5rem'
              }}>
                <div>
                    {/* Show time range if available */}
                    {(comment.timeFrom !== undefined || comment.timeTo !== undefined) && (
                        <div style={{
                            color: 'rgba(212, 175, 55, 1)',
                            fontSize: '0.9rem',
                            marginTop: '0.2rem',
                            fontWeight: 'bold'
                            }}>
                      📍 {comment.timeFrom || '00:00:00.000'} - {comment.timeTo || '00:00:00.000'}
                    </div>
                  )}

                  <strong style={{ color: '#495057', fontSize: '0.9rem' }}>
                    {comment.author}
                  </strong>
                  <div style={{ 
                    color: '#6c757d', 
                    fontSize: '0.8rem',
                    marginTop: '0.2rem'
                  }}>
                    {comment.timestamp}
                  </div>
                  
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering comment click
                    deleteComment(comment.uuid);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    padding: '0.2rem'
                  }}
                  title="Delete comment"
                >
                  ×
                </button>
              </div>
              <p style={{ 
                margin: 0, 
                color: '#212529',
                fontSize: '0.9rem',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap'
              }}>
                {comment.text}
              </p>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Comments;
